import { TalkingAvatarSettingsComponent } from './talking-avatar-settings/talking-avatar-settings.component';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatOption } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatTooltip } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { Subscription } from 'rxjs';

import { BotReferenceMode, botStatuses } from '@core/interfaces/TBot';
import { TBotItem, UiConfiguration, UiInputField } from '@core/interfaces/TBotItem';
import { TFolderItem } from '@core/interfaces/TFolderItem';
import { TProjectItem } from '@core/interfaces/TProjectItem';

import { TBotModel } from '@core/api/bots/types';
import {
  STATIC_ASSIGNMENT_WARNING_THRESHOLDS,
  TContextAssignment,
  TContextGroup,
  TContextItem,
} from '@core/api/context/types';
import { NoticeComponent } from '@shared/components/notice/notice.component';
import { BotsService } from '@shared/services/bots.service';
import { CommonService } from '@shared/services/common.service';
import { ContextService } from '@shared/services/context.service';
import { FoldersService } from '@shared/services/folders.service';
import { ProjectsService } from '@shared/services/projects.service';
import { AppSettingsService } from '@shared/services/settings/app-settings.service';
import { botsTypesForcedStatus } from '@shared/services/settings/types';
import {
  materialsConfigInfoMessage,
  publicationsCountInfoMessage,
} from '@shared/utils/messages';

import { InnerBotsComponent } from '../inner-bots/inner-bots.component';

const BOT_REFERENCE_MODE_OPTIONS: Array<{
  value: BotReferenceMode;
  label: string;
  description: string;
}> = [
  {
    value: 'source_only',
    label: 'Reference to source file only',
    description: 'Show only the source document reference.',
  },
  {
    value: 'all',
    label: 'Source file and ref in ref',
    description: 'Show the source document and the nested reference.',
  },
  {
    value: 'ref_in_ref_only',
    label: 'Reference in reference only',
    description: 'Show only the nested reference when available.',
  },
];

@Component({
  selector: 'app-bot-editor',
  templateUrl: './bot-editor.component.html',
  styleUrls: ['./bot-editor.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatSlideToggle,
    MatSlider,
    MatSliderThumb,
    MatIcon,
    MatButton,
    MatIconButton,
    MatTooltip,
    NgSelectModule,
    NoticeComponent,
    InnerBotsComponent,
    TalkingAvatarSettingsComponent,
  ],
})
export class BotEditorComponent implements OnInit, OnDestroy {
  private USE_ALL_PUBLICATIONS_COUNT = 999;
  public MAX_PUBLICATIONS_LIMIT = 10;
  private _bot: TBotItem = {} as TBotItem;
  protected botsModels: TBotModel[] = [];
  private subs: Subscription[] = [];
  protected readonly botsTypesForcedStatus = botsTypesForcedStatus;
  protected availableFolders: TFolderItem[] = [];
  protected availableProjects: TProjectItem[] = [];
  protected availableContextGroups: TContextGroup[] = [];
  protected availableContextItems: TContextItem[] = [];
  protected staticAssignmentCharCount = 0;
  protected readonly warningThresholds = STATIC_ASSIGNMENT_WARNING_THRESHOLDS;
  @Output() public formValid = new EventEmitter<boolean>();
  @ViewChild(TalkingAvatarSettingsComponent) talkingAvatarSettingsComponent:
    | TalkingAvatarSettingsComponent
    | undefined;
  @Input() public set bot(bot: TBotItem) {
    this._bot = bot;
    this.botEditorForm.patchValue({
      name: bot.name,
      description: bot.description,
      initialPrompt: bot.initialPrompt,
      instruction: bot.instruction ?? '',
      behaviour: bot.behaviour ?? '',
      bot_status: bot.bot_status,
      bot_model: bot.botModel,
      showOnDashboard: bot.showOnDashboard,
      useOwnKnowledge: bot.useOwnKnowledge,
      display_resources: bot.display_resources,
      maximum_resources: bot.maximum_resources,
      extraConfigurations: bot.extraConfigurations,
      descriptionForAI: bot.descriptionForAI,
      maximum_publications:
        bot.maximum_publications === this.USE_ALL_PUBLICATIONS_COUNT
          ? this.MAX_PUBLICATIONS_LIMIT
          : bot.maximum_publications,
      use_all_publications: bot.maximum_publications === this.USE_ALL_PUBLICATIONS_COUNT,
      allowedFolders: (bot.allowedFolders || []) as string[],
      allowedProjects: (bot.allowedProjects || []) as string[],
      referenceCheckPrompt: bot.referenceCheckPrompt || '',
      referenceMode: bot.referenceMode || 'all',
      autoDeleteEnabled: bot.autoDeleteEnabled || false,
    });

    // Clear and repopulate contextAssignments array
    const contextAssignmentsArray = this.contextAssignmentsArray;
    contextAssignmentsArray.clear();
    if (bot.contextAssignments && bot.contextAssignments.length > 0) {
      bot.contextAssignments.forEach(assignment => {
        contextAssignmentsArray.push(this.createContextAssignmentGroup(assignment));
      });
    }
    this.calculateStaticCharCount();

    // Clear inputFields array first
    const inputFieldsArray = this.inputFieldsArray;
    inputFieldsArray.clear();

    // Patch uiConfiguration
    if (bot.uiConfiguration) {
      this.botEditorForm.patchValue({
        uiConfiguration: {
          homepage: bot.uiConfiguration.homepage || { showButton: false, buttonName: '' },
        },
      });

      // Repopulate inputFields array
      if (bot.uiConfiguration.inputFields && bot.uiConfiguration.inputFields.length > 0) {
        bot.uiConfiguration.inputFields.forEach(field => {
          inputFieldsArray.push(this.createInputFieldGroup(field));
        });
      }
    } else {
      // Reset to defaults if no uiConfiguration
      this.botEditorForm.patchValue({
        uiConfiguration: {
          homepage: { showButton: false, buttonName: '' },
        },
      });
    }

    // Lock Instruction and Behaviour unless this is a quiz bot
    const canEditInstructionAndBehaviour = bot.type === 'quizBot';
    if (canEditInstructionAndBehaviour) {
      this.botEditorForm.get('instruction')?.enable();
      this.botEditorForm.get('behaviour')?.enable();
    } else {
      this.botEditorForm.get('instruction')?.disable();
      this.botEditorForm.get('behaviour')?.disable();
    }
  }

  get bot(): TBotItem {
    return this._bot;
  }

  @Input() public hideButtons = false;
  @Output() public cancelCallback = new EventEmitter();
  @Output() public botUpdated = new EventEmitter<TBotItem>();
  protected readonly botStatuses = botStatuses;
  protected readonly referenceModeOptions = BOT_REFERENCE_MODE_OPTIONS;
  publicationsCountInfo = publicationsCountInfoMessage;
  materialsInfo = materialsConfigInfoMessage;
  botConfig = materialsConfigInfoMessage;

  public botEditorForm: FormGroup = this.fb.nonNullable.group({
    name: ['', [Validators.required]],
    description: [''],
    initialPrompt: [''],
    instruction: [''],
    behaviour: [''],
    bot_status: ['in_validation'],
    bot_model: [''],
    display_resources: [false],
    config: [''],
    showOnDashboard: [false],
    useOwnKnowledge: [false],
    descriptionForAI: [''],
    maximum_resources: [0, [Validators.min(0)]],
    maximum_publications: [0, [Validators.min(0)]],
    use_all_publications: [false],
    allowedFolders: [[] as string[]],
    allowedProjects: [[] as string[]],
    referenceCheckPrompt: [''],
    referenceMode: ['all' as BotReferenceMode],
    autoDeleteEnabled: [false],
    extraConfigurations: this.fb.group({
      feedback: this.fb.group({
        behaviour: [''],
        instruction: [''],
        config: [''],
      }),
      initMessage: this.fb.group({
        behaviour: [''],
        instruction: [''],
        config: [''],
      }),
      secondRun: this.fb.group({
        config: [''],
      }),
    }),
    uiConfiguration: this.fb.group({
      homepage: this.fb.group({
        showButton: [false],
        buttonName: [''],
      }),
      inputFields: this.fb.array([]),
    }),
    contextAssignments: this.fb.array([]),
  });

  constructor(
    private fb: FormBuilder,
    public dialog: MatDialog,
    private _commonService: CommonService,
    private _botService: BotsService,
    private _contextService: ContextService,
    private _foldersService: FoldersService,
    private _projectsService: ProjectsService,
    protected appGlobalSettings: AppSettingsService
  ) {}

  ngOnInit() {
    this.subs.push(
      this.botEditorForm.statusChanges.subscribe(() => {
        this.formValid.emit(this.botEditorForm.valid);
      }),
      this._botService.getBotsModelsObservable().subscribe(res => {
        this.botsModels = res;
      }),
      this._foldersService.getFoldersObservable().subscribe(folders => {
        this.availableFolders = folders;
      }),
      this._projectsService.getProjectsObservable().subscribe(projects => {
        this.availableProjects = projects;
      }),
      this._contextService.groups$.subscribe(groups => {
        this.availableContextGroups = groups || [];
      })
    );
    this._botService.getBotModels();
    this._foldersService.fetchFolders();
    this._projectsService.setProjects();
    this._contextService.fetchGroups();

    // Load ALL context items (use dedicated method to avoid shared state issues)
    this._contextService.getAllItems().subscribe({
      next: items => {
        this.availableContextItems = items || [];
      },
      error: err => {
        console.error('Error loading context items:', err);
        this.availableContextItems = [];
      },
    });
  }

  public saveBotConfig(): void {
    this.talkingAvatarSettingsComponent?.storeTalkingAvatarConfig();

    const formVal = this.botEditorForm.value;
    const uiConfiguration = this.buildUiConfiguration(formVal.uiConfiguration);

    // The botCreator architecture uses TWO documents: a Bot document (stores editor fields
    // like instruction, behaviour, config) and a BotsCreator document (stores the inner bots
    // array). The editor always reads from the Bot document, so saves must go through editBot.
    // The editBotCreator path below is kept for cases where the bot object explicitly carries
    // a bots array (e.g. from a BotsCreator-specific view), but in the normal edit-modal flow
    // TBotItem never has a bots property, so this is effectively unused.
    const isBotCreatorParent =
      (this.bot as any).bots !== undefined && !(this.bot as any).botsCreator;

    if (isBotCreatorParent) {
      // Save bot creator PARENT using the creator endpoint
      this._botService.editBotCreator(
        this.bot.botCreatorId || this.bot._id,
        {
          name: formVal.name || '',
          language: (this.bot as any).language || 'en',
          description: formVal.description,
          bot_status: formVal.bot_status,
          botModel: formVal.bot_model,
          display_resources: formVal.display_resources,
          showOnDashboard: formVal.showOnDashboard,
          useOwnKnowledge: formVal.useOwnKnowledge,
          maximum_resources: formVal.maximum_resources,
          promptHints: (this.bot as any).promptHints || [],
          maximum_publications: formVal.use_all_publications
            ? this.USE_ALL_PUBLICATIONS_COUNT
            : formVal.maximum_publications,
          initialPrompt: formVal.initialPrompt,
          referenceCheckPrompt: formVal.referenceCheckPrompt,
          referenceMode: formVal.referenceMode ?? 'all',
          autoDeleteEnabled: formVal.autoDeleteEnabled,
          uiConfiguration,
        },
        () => {
          this.botUpdated.emit(this.bot);
          this._commonService.openSuccessSnackBar('Agent updated successfully');
        }
      );
    } else {
      // Save regular bot using the bot endpoint
      const editPayload: Record<string, unknown> = {
        id: this.bot._id,
        botCreatorId: this.bot._id,
        name: formVal.name,
        description: formVal.description,
        initialPrompt: formVal.initialPrompt,
        showOnDashboard: formVal.showOnDashboard,
        useOwnKnowledge: formVal.useOwnKnowledge,
        bot_status: formVal.bot_status,
        botModel: formVal.bot_model,
        display_resources: formVal.display_resources,
        descriptionForAI: formVal.descriptionForAI,
        maximum_resources: formVal.maximum_resources,
        maximum_publications: formVal.use_all_publications
          ? this.USE_ALL_PUBLICATIONS_COUNT
          : formVal.maximum_publications,
        allowedFolders: formVal.allowedFolders || [],
        allowedProjects: formVal.allowedProjects || [],
        referenceCheckPrompt: formVal.referenceCheckPrompt,
        referenceMode: formVal.referenceMode ?? 'all',
        autoDeleteEnabled: formVal.autoDeleteEnabled,
        contextAssignments: this.getContextAssignmentsValue(),
      };
      if (this.bot.type === 'quizBot') {
        editPayload.instruction = formVal.instruction ?? '';
        editPayload.behaviour = formVal.behaviour ?? '';
      }
      this._botService.editBot(
        {
          ...editPayload,
          extraConfigurations:
            formVal.extraConfigurations
              ? {
                  feedback: formVal.extraConfigurations.feedback,
                  initMessage: formVal.extraConfigurations.initMessage,
                  secondRun: formVal.extraConfigurations.secondRun,
                }
              : undefined,
          uiConfiguration,
        },
        (updated: TBotItem) => {
          this.botUpdated.emit(updated);
          this._commonService.openSuccessSnackBar('Agent updated successfully');
        }
      );
    }
  }

  ngOnDestroy() {
    this.subs.forEach(value => value.unsubscribe());
  }

  private buildUiConfiguration(formUiConfiguration?: {
    homepage?: {
      showButton?: boolean | null;
      buttonName?: string | null;
    } | null;
    inputFields?: UiInputField[] | null;
  }): UiConfiguration | undefined {
    if (!formUiConfiguration) {
      return undefined;
    }

    const homepage = {
      showButton: formUiConfiguration.homepage?.showButton ?? false,
      buttonName: formUiConfiguration.homepage?.buttonName ?? '',
    };
    const inputFields = (formUiConfiguration.inputFields || []) as UiInputField[];
    const hasHomepageConfiguration = homepage.showButton || homepage.buttonName.trim().length > 0;
    const hasExistingUiConfiguration = this.bot.uiConfiguration !== undefined;

    if (!hasExistingUiConfiguration && !hasHomepageConfiguration && inputFields.length === 0) {
      return undefined;
    }

    return {
      homepage,
      inputFields,
    };
  }

  // UI Configuration helpers
  get inputFieldsArray(): FormArray {
    return this.botEditorForm.get('uiConfiguration.inputFields') as FormArray;
  }

  private createInputFieldGroup(field?: UiInputField): FormGroup {
    return this.fb.group({
      id: [field?.id || crypto.randomUUID()],
      type: [field?.type || 'textarea', Validators.required],
      order: [
        field?.order || this.inputFieldsArray.length + 1,
        [Validators.required, Validators.min(1)],
      ],
      show: [field?.show !== undefined ? field.show : true],
      required: [field?.required || false],
      label: [field?.label || ''],
      description: [field?.description || ''],
      buttonText: [field?.buttonText || ''],
      maxFiles: [field?.maxFiles || 5],
      showFilters: [field?.showFilters || false],
      showSourceSelector: [field?.showSourceSelector || false],
      showUpload: [field?.showUpload || false],
      showFolderKeywords: [field?.showFolderKeywords || false],
      showAvatarSwitcher: [field?.showAvatarSwitcher || false],
      showVoiceOption: [field?.showVoiceOption || false],
    });
  }

  addInputField(): void {
    this.inputFieldsArray.push(this.createInputFieldGroup());
  }

  hasFieldType(type: string): boolean {
    return this.inputFieldsArray.controls.some(control => control.get('type')?.value === type);
  }

  removeInputField(index: number): void {
    this.inputFieldsArray.removeAt(index);
  }

  moveInputFieldUp(index: number): void {
    if (index > 0) {
      const field = this.inputFieldsArray.at(index);
      this.inputFieldsArray.removeAt(index);
      this.inputFieldsArray.insert(index - 1, field);
      this.updateFieldOrders();
    }
  }

  moveInputFieldDown(index: number): void {
    if (index < this.inputFieldsArray.length - 1) {
      const field = this.inputFieldsArray.at(index);
      this.inputFieldsArray.removeAt(index);
      this.inputFieldsArray.insert(index + 1, field);
      this.updateFieldOrders();
    }
  }

  private updateFieldOrders(): void {
    this.inputFieldsArray.controls.forEach((control, index) => {
      control.patchValue({ order: index + 1 });
    });
  }

  getInputFieldGroup(index: number): FormGroup {
    return this.inputFieldsArray.at(index) as FormGroup;
  }

  // Context Assignments helpers
  get contextAssignmentsArray(): FormArray {
    return this.botEditorForm.get('contextAssignments') as FormArray;
  }

  private createContextAssignmentGroup(assignment?: TContextAssignment): FormGroup {
    return this.fb.group({
      targetType: [assignment?.targetType || 'group', Validators.required],
      targetId: [assignment?.targetId || '', Validators.required],
      assignmentType: [assignment?.assignmentType || 'dynamic', Validators.required],
    });
  }

  addContextAssignment(): void {
    this.contextAssignmentsArray.push(this.createContextAssignmentGroup());
  }

  removeContextAssignment(index: number): void {
    this.contextAssignmentsArray.removeAt(index);
    this.calculateStaticCharCount();
  }

  getContextAssignmentGroup(index: number): FormGroup {
    return this.contextAssignmentsArray.at(index) as FormGroup;
  }

  onAssignmentChange(): void {
    this.calculateStaticCharCount();
  }

  onTargetTypeChange(index: number): void {
    // Clear the target selection when type changes
    const assignmentGroup = this.getContextAssignmentGroup(index);
    assignmentGroup.get('targetId')?.setValue('');
    this.calculateStaticCharCount();
  }

  calculateStaticCharCount(): void {
    let totalChars = 0;
    const assignments = this.contextAssignmentsArray.value as TContextAssignment[];

    assignments.forEach(assignment => {
      if (assignment.assignmentType === 'static') {
        if (assignment.targetType === 'group') {
          const group = this.availableContextGroups.find(g => g._id === assignment.targetId);
          if (group) {
            // Estimate chars from group instruction + items
            totalChars += group.instruction?.length || 0;
            // Add estimated item content (would need to fetch items for accurate count)
            totalChars += (group.itemCount || 0) * 500; // Rough estimate
          }
        } else {
          const item = this.availableContextItems.find(i => i._id === assignment.targetId);
          if (item) {
            totalChars +=
              item.question.length + item.content.length + (item.instruction?.length || 0);
          }
        }
      }
    });

    this.staticAssignmentCharCount = totalChars;
  }

  getStaticWarningLevel(): 'none' | 'yellow' | 'orange' | 'red' {
    if (this.staticAssignmentCharCount >= this.warningThresholds.red) {
      return 'red';
    } else if (this.staticAssignmentCharCount >= this.warningThresholds.orange) {
      return 'orange';
    } else if (this.staticAssignmentCharCount >= this.warningThresholds.yellow) {
      return 'yellow';
    }
    return 'none';
  }

  getStaticWarningMessage(): string {
    const level = this.getStaticWarningLevel();
    const tokens = Math.round(this.staticAssignmentCharCount / 4);
    switch (level) {
      case 'red':
        return `Warning: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens). This is very large and may impact performance.`;
      case 'orange':
        return `Caution: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens). Consider using dynamic assignment.`;
      case 'yellow':
        return `Note: ${this.staticAssignmentCharCount.toLocaleString()} chars (~${tokens.toLocaleString()} tokens).`;
      default:
        return '';
    }
  }

  private getContextAssignmentsValue(): TContextAssignment[] | undefined {
    const assignments = this.contextAssignmentsArray.value as TContextAssignment[];
    if (!assignments || assignments.length === 0) {
      return undefined;
    }
    return assignments.filter(a => a.targetId && a.targetType && a.assignmentType);
  }
}
